import { FormNotice } from '@zappyrent/hundop';
import { upperFirst } from 'lodash';
import { useEffect } from 'react';
import { Control, Controller, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IListingForm } from '../../../pages/ListingDetail/ListingDetail.types';
import TextInput from '../../___design/TextInput';
import { StyledListingAddAnOwnerBody } from './ListingAddAnOwner.styles';
import { IListingAddAnOwnerForm } from './ListingAddAnOwner.types';
import { filterRequiredFieldForAddAnOwner } from './utils/filterRequiredFieldForAddAnOwner';

const ListingAddAnOwner = ({
  control,
  index,
  watch,
  handleMissingOwnerInformation,
}: {
  control: Control<IListingForm>;
  watch: UseFormWatch<IListingForm>;
  index: number;
  handleMissingOwnerInformation: (
    index: number,
    missingFields: {
      label: string;
      fields: string[];
    },
  ) => void;
}) => {
  const { t } = useTranslation(['listingDetail']);
  const values = watch();
  const owners = values?.owners ?? [];
  useEffect(() => {
    const missingValue = filterRequiredFieldForAddAnOwner({
      index,
      owner: owners[index] as IListingAddAnOwnerForm,
    });
    handleMissingOwnerInformation(index, {
      ...missingValue,
    });
  }, [JSON.stringify(owners[index])]);

  return (
    <StyledListingAddAnOwnerBody>
      <Controller
        control={control}
        name={`owners.${index}.first_name`}
        rules={{
          required: {
            value: true,
            message: upperFirst(t('formError:requiredField')),
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <TextInput
              id="first_name"
              name="first_name"
              label={upperFirst(t('listingDetail:name'))}
              value={value ?? ''}
              onChange={onChange}
              required
            >
              {error?.message && (
                <FormNotice isWrapped={true}>{error?.message}</FormNotice>
              )}
            </TextInput>
          );
        }}
      />
      <Controller
        control={control}
        name={`owners.${index}.last_name`}
        rules={{
          required: {
            value: true,
            message: upperFirst(t('formError:requiredField')),
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextInput
            id="last_name"
            name="last_name"
            label={upperFirst(t('listingDetail:surname'))}
            value={value ?? ''}
            onChange={onChange}
            required
          >
            {error?.message && (
              <FormNotice isWrapped={true}>{error?.message}</FormNotice>
            )}
          </TextInput>
        )}
      />
      <Controller
        control={control}
        name={`owners.${index}.date_of_birth`}
        render={({ field: { onChange, value } }) => {
          return (
            <TextInput
              id="date_of_birth"
              name="date_of_birth"
              label={upperFirst(t('listingDetail:birthday'))}
              value={value ?? ''}
              onChange={onChange}
              type="date"
            />
          );
        }}
      />
      <Controller
        control={control}
        name={`owners.${index}.city`}
        render={({ field: { onChange, value } }) => (
          <TextInput
            id="city"
            name="city"
            label={upperFirst(t('listingDetail:residence'))}
            value={value ?? ''}
            onChange={onChange}
          />
        )}
      />
      <Controller
        control={control}
        name={`owners.${index}.id_document_number`}
        rules={{
          required: {
            value: true,
            message: upperFirst(t('formError:requiredField')),
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextInput
            id="id_document_number"
            name="id_document_number"
            label={upperFirst(t('listingDetail:documentNumber'))}
            value={value ?? ''}
            onChange={onChange}
            required
          >
            {error?.message && (
              <FormNotice isWrapped={true}>{error?.message}</FormNotice>
            )}
          </TextInput>
        )}
      />
      <Controller
        control={control}
        name={`owners.${index}.id_document_expiry_date`}
        render={({ field: { onChange, value } }) => (
          <TextInput
            id="id_document_expiry_date"
            name="id_document_expiry_date"
            label={upperFirst(t('listingDetail:documentExpiryDate'))}
            value={value ?? ''}
            onChange={onChange}
            type="date"
          />
        )}
      />

      <Controller
        control={control}
        name={`owners.${index}.tax_code`}
        rules={{
          required: {
            value: true,
            message: upperFirst(t('formError:requiredField')),
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextInput
            id="tax_code"
            name="tax_code"
            label={upperFirst(t('listingDetail:taxCode'))}
            value={value ?? ''}
            onChange={onChange}
            required
          >
            {error?.message && (
              <FormNotice isWrapped={true}>{error?.message}</FormNotice>
            )}
          </TextInput>
        )}
      />
    </StyledListingAddAnOwnerBody>
  );
};

export default ListingAddAnOwner;
